import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AI_ALLOWED_EXTENSIONS,
  AI_ATTACHMENT_MAX_SIZE,
} from '@/constant/approval';
import FileUpload, { FileUploadProps } from './FileUpload';
import {
  AiCancelButton,
  AiLoadingOverlay,
  AiLoadingText,
  AiNotice,
  AiSpinner,
  AiTag,
} from './styles';

export type AiFileAttachmentProps = Omit<
  FileUploadProps,
  'variant' | 'multiple' | 'topSlot' | 'overlay' | 'footer'
> & {
  /** 박스 상단 태그. 기본 'AI 정리 도우미', 비표시는 false */
  tagLabel?: ReactNode | false;
  /** 분석 중 — 오버레이로 박스를 덮고 취소 버튼 노출 */
  analyzing?: boolean;
  onCancel?: () => void;
  /** 박스 아래 안내. 비표시는 false */
  notice?: ReactNode | false;
};

/**
 * AI 첨부 UI — 단건, 보라 액센트. 업로드·분석 로직은 갖지 않음(useAiFileExtraction 등 호출부 담당).
 * 헤더(첨부파일 라벨·체크박스)는 기본 숨김.
 */
const AiFileAttachment = ({
  allowedExtensions = AI_ALLOWED_EXTENSIONS,
  maxSize = AI_ATTACHMENT_MAX_SIZE,
  label = false,
  headerRight = false,
  helpSubText,
  tagLabel,
  analyzing = false,
  onCancel,
  notice,
  ...rest
}: AiFileAttachmentProps) => {
  const { t } = useTranslation();
  const resolvedTag = tagLabel === undefined ? t('AI 정리 도우미') : tagLabel;
  const resolvedNotice =
    notice === undefined
      ? t('AI는 실수할 수 있으니 결과를 다시 한번 확인하세요.')
      : notice;

  return (
    <FileUpload
      variant="ai"
      multiple={false}
      label={label}
      headerRight={headerRight}
      allowedExtensions={allowedExtensions}
      maxSize={maxSize}
      accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
      helpSubText={
        helpSubText ??
        t('파일당 최대 {{mb}}MB / {{formats}}', {
          mb: Math.floor(maxSize / 1024 / 1024),
          formats: allowedExtensions.join(', '),
        })
      }
      topSlot={resolvedTag === false ? undefined : <AiTag>{resolvedTag}</AiTag>}
      overlay={
        analyzing ? (
          <AiLoadingOverlay onClick={(e) => e.stopPropagation()}>
            <AiLoadingText>
              <span>{t('AI가 정리하고 있어요.')}</span>
              <span>{t('길이에 따라 1분 정도 소요될 수 있어요.')}</span>
              <span className="block">
                {t('분석이 길어질 경우, 파일을 나눠서 업로드 해 주세요.')}
              </span>
            </AiLoadingText>
            {onCancel && (
              <AiCancelButton type="button" onClick={onCancel}>
                <AiSpinner />
                {t('분석 취소')}
              </AiCancelButton>
            )}
          </AiLoadingOverlay>
        ) : undefined
      }
      footer={
        resolvedNotice === false ? undefined : (
          <AiNotice>{resolvedNotice}</AiNotice>
        )
      }
      {...rest}
    />
  );
};

export default AiFileAttachment;
